import { isAnswered, type AnswerKey, type EngineSnapshot, type NodeId } from '../engine';
import { hasOwnAmount, placeOf } from './view';

/**
 * Wat er nog open staat voordat een opname wordt afgerond of ingediend - lokaal
 * berekend met dezelfde regels als het waarschuwingsrapport van de API
 * (`CompleteInspection::buildReport`), zodat de inspecteur het ook zonder
 * verbinding ziet:
 *
 * - open vragen: zichtbare vragen zonder antwoord, per onderdeel;
 * - ontbrekende foto's: een beantwoorde zichtbare vraag met `Foto verplicht` en
 *   minder dan `photo_min` (minstens één) foto's. Elke vraag telt één keer,
 *   bij het eerste onderdeel waar hij staat;
 * - ontbrekende bedragen: een gevolgde keuze zonder vast bedrag die de inspecteur
 *   om het bedrag vraagt (`amount_prompt`), terwijl het antwoord nog geen eigen
 *   bedrag heeft. Elk antwoord telt één keer, bij het eerste onderdeel in de boom
 *   dat die keuze volgt (`RecalculateInspection::amountsMissing`). Het houdt het
 *   indienen niet tegen: tot hij het invult, telt die keuze geen geld.
 */

export interface CompletionNode {
    nodeId: NodeId;
    /** "25 Dakbedekking · hoofddak" */
    place: string;
    unanswered: number;
    photosMissing: number;
}

/** Een antwoord waarvan de keuze om een bedrag vraagt dat nog niet is ingevuld. */
export interface CompletionAmount {
    key: AnswerKey;
    nodeId: NodeId;
    /** "14.11 Staat van de airconditioning" */
    place: string;
    /** De gekozen keuze, zoals de inspecteur hem aantikte. */
    label: string;
}

export interface CompletionReport {
    questionsVisible: number;
    questionsAnswered: number;
    questionsUnanswered: number;
    photosRequired: number;
    photosMissing: number;
    /** De antwoorden waarbij de inspecteur het bedrag nog moet invullen, in boomvolgorde. */
    amountsMissing: CompletionAmount[];
    /** Alleen de onderdelen waar iets open staat, in boomvolgorde. */
    nodes: CompletionNode[];
    hasWarnings: boolean;
}

export function completionReport(
    snapshot: EngineSnapshot,
    photoCounts: ReadonlyMap<AnswerKey, number>,
): CompletionReport {
    const counted = new Set<AnswerKey>();
    const missingByNode = new Map<NodeId, number>();
    let photosRequired = 0;
    let photosMissing = 0;

    for (const nodeId of snapshot.tree.order) {
        const node = snapshot.visibility.nodes.get(nodeId);

        node?.questionKeys.forEach((key, index) => {
            if (counted.has(key)) {
                return;
            }

            counted.add(key);

            const question = snapshot.graph.questionsById.get(node.questionIds[index] ?? -1);

            if (
                !question ||
                question.question.photo_requirement !== 'required' ||
                !isAnswered(question, snapshot.answers[key])
            ) {
                return;
            }

            photosRequired += 1;

            if ((photoCounts.get(key) ?? 0) < Math.max(1, question.question.photo_min ?? 1)) {
                photosMissing += 1;
                missingByNode.set(nodeId, (missingByNode.get(nodeId) ?? 0) + 1);
            }
        });
    }

    const amountsMissing: CompletionAmount[] = [];
    const priced = new Set<AnswerKey>();

    for (const nodeId of snapshot.tree.order) {
        for (const chosen of snapshot.visibility.nodes.get(nodeId)?.chosen ?? []) {
            if (!chosen.option.amount_prompt || priced.has(chosen.questionKey)) {
                continue;
            }

            priced.add(chosen.questionKey);

            if (hasOwnAmount(snapshot.answers[chosen.questionKey])) {
                continue;
            }

            const option = snapshot.graph.questionsById
                .get(chosen.questionId)
                ?.question.options.find((candidate) => candidate.id === chosen.questionOptionId);

            amountsMissing.push({
                key: chosen.questionKey,
                nodeId,
                place: placeOf(snapshot, nodeId),
                label: option?.label ?? '',
            });
        }
    }

    const nodes: CompletionNode[] = [];

    for (const nodeId of snapshot.tree.order) {
        const progress = snapshot.progress.nodes.get(nodeId);
        const unanswered = progress
            ? Math.max(0, progress.visibleCount - progress.answeredCount)
            : 0;
        const missing = missingByNode.get(nodeId) ?? 0;

        if (unanswered > 0 || missing > 0) {
            nodes.push({
                nodeId,
                place: placeOf(snapshot, nodeId),
                unanswered,
                photosMissing: missing,
            });
        }
    }

    const questionsVisible = snapshot.progress.visibleQuestions;
    const questionsAnswered = snapshot.progress.answeredQuestions;
    const questionsUnanswered = Math.max(0, questionsVisible - questionsAnswered);

    return {
        questionsVisible,
        questionsAnswered,
        questionsUnanswered,
        photosRequired,
        photosMissing,
        amountsMissing,
        nodes,
        hasWarnings:
            questionsUnanswered > 0 ||
            photosMissing > 0 ||
            amountsMissing.length > 0 ||
            nodes.length > 0,
    };
}
